import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { type PaginatedResponse } from '../../../api/client'
import {
  createContactApi,
  updateContactApi,
  patchContactApi,
  deleteContactApi,
  deleteContactsApi,
} from '../api/contactApi'
import { type Contact } from '../../../utils/types'
import LogTool from '../../../logger/logTools'
import { contactKeys } from '../utils/queryKeys'
import { customerKeys } from '../../Customer/utils/queryKeys'
import { supplierKeys } from '../../Supplier/utils/queryKeys'
import { getPKfromSelf } from '../../../utils/functions'

const log = new LogTool({ context: 'ContactMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new contact
 */
export function useCreateContact(): UseMutationResult<Contact, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createContactApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: supplierKeys.lists() })
      log.info('Contact created successfully')
    },
    onError: (error) => {
      log.error('Error creating contact:', error)
    },
  })
}

/**
 * Hook to update a contact (full update with PUT)
 */
export function useUpdateContact(): UseMutationResult<
  Contact,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  const updateCachedContact = (updatedContact: Contact): void => {
    // update cached list responses so UI can use fresh data without waiting for a refetch
    queryClient.setQueriesData<PaginatedResponse<Contact>>(
      { queryKey: contactKeys.lists() },
      oldData => {
        if (!oldData?.results) return oldData
        return {
          ...oldData,
          results: oldData.results.map(contact =>
            contact.self === updatedContact.self ? updatedContact : contact
          ),
        }
      }
    )

    // update any cached detail queries for the same contact (regardless of expand params)
    queryClient.setQueriesData<Contact>(
      { queryKey: contactKeys.details() },
      cachedContact => (cachedContact?.self === updatedContact.self ? updatedContact : cachedContact)
    )
  }

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateContactApi(idOrSelf, data),
    onSuccess: async (updatedContact) => {
      updateCachedContact(updatedContact)
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: contactKeys.details() })
      await queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: supplierKeys.lists() })
      log.info('Contact updated successfully')
    },
    onError: (error) => {
      log.error('Error updating contact:', error)
    },
  })
}

/**
 * Hook to patch a contact (partial update with PATCH)
 */
export function usePatchContact(): UseMutationResult<
  Contact,
  Error,
  { idOrSelf: string | number; data: Partial<any> }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => patchContactApi(idOrSelf, data),
    onSuccess: async (updatedContact) => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({
        queryKey: contactKeys.detail(Number(getPKfromSelf(updatedContact.self)))
      })
      await queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: supplierKeys.lists() })
      log.info('Contact patched successfully')
    },
    onError: (error) => {
      log.error('Error patching contact:', error)
    },
  })
}

/**
 * Hook to delete a single contact
 */
export function useDeleteContact(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteContactApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: supplierKeys.lists() })
      log.info('Contact deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting contact:', error)
    },
  })
}

/**
 * Hook to delete multiple contacts
 */
export function useDeleteContacts(): UseMutationResult<void, Error, Contact[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteContactsApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
      await queryClient.invalidateQueries({ queryKey: supplierKeys.lists() })
      log.info('Contacts deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting contacts:', error)
    },
  })
}
